/**
 * FinancementBadges — official partner logos for the 4 funding routes.
 * Files live in /public/financeurs/ so they are served at the root path
 * (no CDN dependency, fast first paint, no risk of an upstream 404).
 *
 * Logo provenance:
 *   - France Travail : Wikimedia Commons (CC) — `france-travail.png` 1280×454
 *   - AGEFIPH        : agefiph.fr official site SVG
 *   - FAFCEA         : fafcea.com official site SVG
 *   - OPCO · PTP     : inline mark (no single national OPCO logo)
 */

const PartnerLogo = ({ src, alt, size = 44, className = '' }) => (
  <img
    src={src}
    alt={alt}
    style={{ height: size, width: 'auto', maxWidth: size * 3.2 }}
    className={`object-contain ${className}`}
    loading="lazy"
  />
);

export const FranceTravailBadge = ({ size = 44, className = '' }) => (
  <PartnerLogo src="/financeurs/france-travail.png" alt="France Travail" size={size} className={className} />
);

export const FafceaBadge = ({ size = 44, className = '' }) => (
  <PartnerLogo src="/financeurs/fafcea.svg" alt="FAFCEA — Fonds d'Assurance Formation" size={size} className={className} />
);

export const AgefiphBadge = ({ size = 44, className = '' }) => (
  <PartnerLogo src="/financeurs/agefiph.svg" alt="AGEFIPH" size={size} className={className} />
);

// OPCO has no unified national logo — keep the existing inline mark.
export const OpcoBadge = ({ size = 44, className = '' }) => (
  <svg
    width={size * 2.6}
    height={size}
    viewBox="0 0 130 50"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect x="0" y="0" width="50" height="50" rx="10" fill="#1B5E84" />
    <text x="25" y="32" fontSize="13" fontWeight="800" textAnchor="middle" fill="#FFFFFF" fontFamily="Arial, sans-serif">OPCO</text>
    <text x="60" y="22" fontSize="11" fontWeight="700" fill="#1B5E84" fontFamily="Arial, sans-serif">OPCO · PTP</text>
    <text x="60" y="36" fontSize="8" fill="#1B5E84AA" fontFamily="Arial, sans-serif">Salariés</text>
  </svg>
);

// NOTE: France Travail, FAFCEA and AGEFIPH partner cards are temporarily
// hidden at the client's request. Toggle `HIDE_PARTNER_KEYS` to bring them
// back without restoring the code.
const HIDE_PARTNER_KEYS = new Set(['france-travail', 'fafcea', 'agefiph']);

const _ALL_FINANCEMENT_OPTIONS = [
  {
    key: 'france-travail',
    Badge: FranceTravailBadge,
    title: 'France Travail',
    subtitle: "Demandeurs d'emploi · AIF",
    href: 'https://www.francetravail.fr/accueil/',
    color: '#5d2e8c',
  },
  {
    key: 'fafcea',
    Badge: FafceaBadge,
    title: 'FAFCEA',
    subtitle: 'Artisans · chefs d\'entreprise',
    href: 'https://www.fafcea.com/',
    color: '#0E3A53',
  },
  {
    key: 'opco-ptp',
    Badge: OpcoBadge,
    title: 'OPCO · Projet de Transition Pro',
    subtitle: 'Salariés',
    href: 'https://www.transitionspro.fr/',
    color: '#1B5E84',
  },
  {
    key: 'agefiph',
    Badge: AgefiphBadge,
    title: 'AGEFIPH',
    subtitle: 'Personnes en situation de handicap',
    href: 'https://www.agefiph.fr/',
    color: '#0073A8',
  },
];

export const FINANCEMENT_OPTIONS = _ALL_FINANCEMENT_OPTIONS.filter(
  (opt) => !HIDE_PARTNER_KEYS.has(opt.key)
);
